import { type JSX, useCallback, useEffect, useState } from 'react';

import { ApiFehler } from '../api/fehler';
import {
  type Artikelzeile,
  type Poolressource,
  type Zusatzangebot,
  type Zusatzauswahl,
  liste as artikelListe,
  zusatzangebote as holeAngebote,
  zusatzangebotEntfernen,
  zusatzangebotSetzen,
  zusatzpool as holePool,
  zusatzpoolEntfernen,
  zusatzpoolHinzufuegen,
} from '../api/produkte';
import { type Ressourcenzeile, liste as ressourcenListe } from '../api/ressourcen';
import { Meldung } from '../komponenten/Meldung';
import { Rueckfrage } from '../komponenten/Rueckfrage';
import { Abschnitt } from '../komponenten/Akte';

const AUSWAHLNAME: Readonly<Record<Zusatzauswahl, string>> = {
  frei: 'frei wählbar',
  vorausgewaehlt: 'vorausgewählt',
  verpflichtend: 'verpflichtend',
};

/**
 * Welche Zusatzleistungen zu diesem Produkt angeboten werden (BUC-84 i. d. F.
 * ÄA-31).
 *
 * Das Angebot steht je Hauptprodukt. Solange hier nichts steht, ist im
 * Innendienst jede Zusatzleistung wählbar; sobald eine Zeile steht, nur noch
 * die angebotenen. Das sagt die Maske auch, denn die erste Zeile ändert, was
 * am Telefon noch geht.
 */
export function Zusatzangebote({
  artikel,
  darfAendern,
}: {
  readonly artikel: string;
  readonly darfAendern: boolean;
}): JSX.Element {
  const [angebote, setzeAngebote] = useState<readonly Zusatzangebot[] | undefined>(undefined);
  const [kandidaten, setzeKandidaten] = useState<readonly Artikelzeile[]>([]);
  const [neu, setzeNeu] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  /* CRM-104: das Angebot, dessen Entfernen im Pop-up nachfragt. */
  const [zuEntfernen, setzeZuEntfernen] = useState<Zusatzangebot | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const [a, k] = await Promise.all([
        holeAngebote(artikel),
        artikelListe({ vertriebsform: 'zusatzleistung', status: 'aktiv' }),
      ]);
      setzeAngebote(a);
      setzeKandidaten(k.artikel.filter((z) => z.id !== artikel));
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [artikel]);

  useEffect(() => {
    void laden();
  }, [laden]);

  /** Wirft (CRM-104): Aus der Rückfrage heraus steht der Fehler im Pop-up. */
  async function schreibe(was: () => Promise<readonly Zusatzangebot[]>): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      setzeAngebote(await was());
    } finally {
      setzeLaeuft(false);
    }
  }

  async function fuehreAus(was: () => Promise<readonly Zusatzangebot[]>): Promise<void> {
    try {
      await schreibe(was);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  const setze = (a: Zusatzangebot, teil: Partial<Zusatzangebot>): Promise<void> => {
    const z = { ...a, ...teil };
    return fuehreAus(() =>
      zusatzangebotSetzen(artikel, a.zusatz, {
        auswahl: z.auswahl,
        buchungsstrecke: z.buchungsstrecke,
        reihenfolge: z.reihenfolge,
      }),
    );
  };

  if (angebote === undefined) {
    return fehler !== undefined ? <Meldung fehler={fehler} /> : <p role="status">Wird geladen …</p>;
  }

  const angeboten = new Set(angebote.map((a) => a.zusatz));
  const waehlbar = kandidaten.filter((k) => !angeboten.has(k.id));

  /* ÄA-41: ein aufklappbarer Abschnitt im Reiter „Programm" der Artikelakte. */
  return (
    // BUC-84
    <Abschnitt titel="Zusatzleistungen" zusatz={angebote.length}>

      {fehler !== undefined && <Meldung fehler={fehler} />}

      <p className="hinweiszeile">
        {angebote.length === 0
          ? 'Für dieses Produkt ist noch nichts festgelegt — im Innendienst ist jede Zusatzleistung wählbar.'
          : 'Zu diesem Produkt sind nur diese Zusatzleistungen wählbar.'}
      </p>

      {angebote.length > 0 && (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th>Zusatzleistung</th>
                <th>Auswahl</th>
                <th>Buchungsstrecke</th>
                <th>Verfügbarkeit</th>
                {darfAendern && <th aria-label="Handlungen" />}
              </tr>
            </thead>
            <tbody>
              {angebote.map((a) => (
                <tr key={a.zusatz}>
                  <td>
                    {a.bezeichnung}
                    <span className="kanalliste__name"> · {a.artikelnummer}</span>
                  </td>
                  <td>
                    <select
                      className="feld__eingabe"
                      aria-label={`Auswahl ${a.bezeichnung}`}
                      value={a.auswahl}
                      disabled={!darfAendern || laeuft}
                      onChange={(e) => void setze(a, { auswahl: e.target.value as Zusatzauswahl })}
                    >
                      {(Object.keys(AUSWAHLNAME) as Zusatzauswahl[]).map((w) => (
                        <option key={w} value={w}>
                          {AUSWAHLNAME[w]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    {/* ÄA-31: das Feld, das den Zusatz in die Buchungsstrecke nimmt. */}
                    <input
                      type="checkbox"
                      aria-label={`${a.bezeichnung} in der Buchungsstrecke anbieten`}
                      checked={a.buchungsstrecke}
                      disabled={!darfAendern || laeuft}
                      onChange={(e) => void setze(a, { buchungsstrecke: e.target.checked })}
                    />
                  </td>
                  <td>{a.poolgroesse === 0 ? 'unbegrenzt' : `Pool aus ${a.poolgroesse}`}</td>
                  {darfAendern && (
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        disabled={laeuft}
                        onClick={() => setzeZuEntfernen(a)}
                      >
                        Entfernen …
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* AeA-31, Nachtrag: Der Mitgliedspreis ist ein Preis der Liste „Mitgliederpreise". */}
      <p className="feld__hilfe">
        Die Preise stehen an der Zusatzleistung selbst, im Reiter „Preise": in der
        Standardpreisliste und — wo abweichend — in den Mitgliederpreisen.
      </p>

      {darfAendern && (
        <div className="feldreihe">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="zaNeu">
              Zusatzleistung anbieten
            </label>
            <select
              id="zaNeu"
              className="feld__eingabe"
              value={neu}
              onChange={(e) => setzeNeu(e.target.value)}
            >
              <option value="">— wählen —</option>
              {waehlbar.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.bezeichnung} ({k.artikelnummer})
                </option>
              ))}
            </select>
            {kandidaten.length === 0 && (
              <p className="feld__hilfe">
                Es gibt noch keinen aktiven Artikel mit der Vertriebsform „Zusatzleistung".
              </p>
            )}
          </div>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={neu === '' || laeuft}
              onClick={() =>
                void fuehreAus(() =>
                  zusatzangebotSetzen(artikel, neu, {
                    auswahl: 'frei',
                    buchungsstrecke: false,
                    reihenfolge: angebote.length,
                  }),
                ).then(() => setzeNeu(''))
              }
            >
              Anbieten
            </button>
          </div>
        </div>
      )}

      {zuEntfernen !== null && (
        <Rueckfrage
          frage="Zusatzleistung entfernen?"
          unterzeile={`${zuEntfernen.bezeichnung} · ${zuEntfernen.artikelnummer}`}
          tat="Entfernen"
          beiBestaetigen={async () => {
            await schreibe(() => zusatzangebotEntfernen(artikel, zuEntfernen.zusatz));
            setzeZuEntfernen(null);
          }}
          beiSchliessen={() => setzeZuEntfernen(null)}
        >
          {/* BUC-84: Ohne jede Zeile ist im Innendienst wieder jede Zusatzleistung wählbar. */}
          <p>
            Sie wird zu diesem Produkt nicht mehr angeboten
            {zuEntfernen.buchungsstrecke ? ', auch nicht in der Buchungsstrecke' : ''}; Auswahl und
            Reihenfolge gehen verloren.{' '}
            {angebote.length === 1
              ? 'Es ist die letzte Zeile — danach ist im Innendienst wieder jede Zusatzleistung wählbar.'
              : ''}
          </p>
        </Rueckfrage>
      )}
    </Abschnitt>
  );
}

/**
 * Der Pool hinter einer Zusatzleistung (BUC-85 i. d. F. ÄA-31).
 *
 * Ein Mietwagen ist ein Pool echter Fahrzeuge: Wer den Zusatz wählt, belegt
 * einen freien Wagen für die Dauer des Termins. Ohne Pool ist der Zusatz
 * unbegrenzt.
 */
export function Zusatzpool({
  zusatz,
  darfAendern,
}: {
  readonly zusatz: string;
  readonly darfAendern: boolean;
}): JSX.Element {
  const [pool, setzePool] = useState<readonly Poolressource[] | undefined>(undefined);
  const [kandidaten, setzeKandidaten] = useState<readonly Ressourcenzeile[]>([]);
  const [neu, setzeNeu] = useState('');
  const [fehler, setzeFehler] = useState<ApiFehler | undefined>(undefined);
  const [laeuft, setzeLaeuft] = useState(false);
  /* CRM-104: die Ressource, deren Entfernen im Pop-up nachfragt. */
  const [zuEntfernen, setzeZuEntfernen] = useState<Poolressource | null>(null);

  const laden = useCallback(async (): Promise<void> => {
    try {
      const [p, r] = await Promise.all([
        holePool(zusatz),
        ressourcenListe({ arten: ['fahrzeug', 'betriebsmittel'] }),
      ]);
      setzePool(p);
      setzeKandidaten(r.filter((x) => x.belegungsart === 'ausschliesslich'));
      setzeFehler(undefined);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }, [zusatz]);

  useEffect(() => {
    void laden();
  }, [laden]);

  /** Wirft (CRM-104): Aus der Rückfrage heraus steht der Fehler im Pop-up. */
  async function schreibe(was: () => Promise<readonly Poolressource[]>): Promise<void> {
    setzeLaeuft(true);
    setzeFehler(undefined);
    try {
      setzePool(await was());
    } finally {
      setzeLaeuft(false);
    }
  }

  async function fuehreAus(was: () => Promise<readonly Poolressource[]>): Promise<void> {
    try {
      await schreibe(was);
    } catch (ursache) {
      setzeFehler(ursache as ApiFehler);
    }
  }

  if (pool === undefined) {
    return fehler !== undefined ? <Meldung fehler={fehler} /> : <p role="status">Wird geladen …</p>;
  }

  const imPool = new Set(pool.map((p) => p.ressource));
  const waehlbar = kandidaten.filter((k) => !imPool.has(k.id));

  return (
    // BUC-85
    <Abschnitt titel="Pool" zusatz={pool.length}>

      {fehler !== undefined && <Meldung fehler={fehler} />}

      <p className="hinweiszeile">
        {pool.length === 0
          ? 'Kein Pool — diese Zusatzleistung ist unbegrenzt buchbar.'
          : `Buchbar, solange eine von ${pool.length} Ressourcen zum Termin frei ist.`}
      </p>

      {pool.length > 0 && (
        <div className="tabellenhuelle tabellenhuelle--telefon">
          <table className="liste__tabelle">
            <thead>
              <tr>
                <th>Ressource</th>
                <th>Kennung</th>
                {darfAendern && <th aria-label="Handlungen" />}
              </tr>
            </thead>
            <tbody>
              {pool.map((p) => (
                <tr key={p.ressource}>
                  <td>{p.bezeichnung}</td>
                  <td className="liste__nummer">{p.kennung}</td>
                  {darfAendern && (
                    <td>
                      <button
                        type="button"
                        className="knopf knopf--eingriff knopf--klein"
                        disabled={laeuft}
                        onClick={() => setzeZuEntfernen(p)}
                      >
                        Entfernen …
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {darfAendern && (
        <div className="feldreihe">
          <div className="feld">
            <label className="feld__beschriftung" htmlFor="zpNeu">
              Ressource in den Pool
            </label>
            <select
              id="zpNeu"
              className="feld__eingabe"
              value={neu}
              onChange={(e) => setzeNeu(e.target.value)}
            >
              <option value="">— wählen —</option>
              {waehlbar.map((k) => (
                <option key={k.id} value={k.id}>
                  {k.bezeichnung} ({k.kennung})
                </option>
              ))}
            </select>
            {kandidaten.length === 0 && (
              <p className="feld__hilfe">
                Es ist noch kein Fahrzeug und kein Betriebsmittel angelegt, das ganz belegt wird.
              </p>
            )}
          </div>
          <div className="knopfreihe">
            <button
              type="button"
              className="knopf knopf--haupt"
              disabled={neu === '' || laeuft}
              onClick={() =>
                void fuehreAus(() => zusatzpoolHinzufuegen(zusatz, neu)).then(() => setzeNeu(''))
              }
            >
              Hinzufügen
            </button>
          </div>
        </div>
      )}

      {zuEntfernen !== null && (
        <Rueckfrage
          frage="Ressource aus dem Pool entfernen?"
          unterzeile={`${zuEntfernen.bezeichnung} · ${zuEntfernen.kennung}`}
          tat="Entfernen"
          beiBestaetigen={async () => {
            await schreibe(() => zusatzpoolEntfernen(zusatz, zuEntfernen.ressource));
            setzeZuEntfernen(null);
          }}
          beiSchliessen={() => setzeZuEntfernen(null)}
        >
          {/* BUC-85: Ohne Pool ist der Zusatz unbegrenzt buchbar. */}
          <p>
            {pool.length === 1
              ? 'Es ist die letzte Ressource — danach hat die Zusatzleistung keinen Pool mehr und ist unbegrenzt buchbar.'
              : pool.length === 2
                ? 'Danach ist die Zusatzleistung nur noch buchbar, solange die verbleibende Ressource zum Termin frei ist.'
                : `Danach ist die Zusatzleistung buchbar, solange eine von ${String(pool.length - 1)} Ressourcen zum Termin frei ist.`}
          </p>
        </Rueckfrage>
      )}
    </Abschnitt>
  );
}
